/**
 * userService.ts
 * 
 * Service for managing user profiles in Firebase Firestore
 */
import { 
  getFirestore, 
  doc, 
  getDoc, 
  updateDoc, 
  runTransaction,
  collection,
  getDocs,
  query,
  orderBy,
  setDoc,
  deleteDoc
} from 'firebase/firestore';
import { User, getAuth } from 'firebase/auth';
import { httpsCallable, getFunctions } from 'firebase/functions';
import { app } from '../firebase';
import { logger } from '../utils/logger';
import { 
  UserProfile, 
  UserRole, 
  MembershipStatus, 
  USERNAME_REGEX, 
  USERNAME_MIN_LENGTH,
  USERNAME_MAX_LENGTH,
  RESERVED_USERNAMES 
} from '../types/user';

// Initialize Firebase services
const db = getFirestore(app);
const functions = getFunctions(app);

/**
 * Ensures a user has a role property set to 'user' if it's undefined
 * This helps with Firestore security rules that check role property
 * @param uid User ID to check and update
 */
export const ensureUserRole = async (uid: string): Promise<void> => {
  try {
    const userRef = doc(db, 'users', uid);
    const userSnap = await getDoc(userRef);
    
    if (userSnap.exists()) {
      const userData = userSnap.data();
      // If role is missing or undefined, set it to 'user'
      if (!userData.role) {
        try {
          await updateDoc(userRef, { 
            role: 'user',
            updatedAt: Date.now() 
          });
          logger.info('✅ User role initialized', { uid });
        } catch (updateError: any) {
          // If permission denied, try to create the user document first
          if (updateError.code === 'permission-denied') {
            logger.warn('⚠️ Permission denied when updating user role, trying to create profile first', { uid });
            
            // Create user profile with default values if it doesn't exist
            try {
              const auth = getAuth();
              const currentUser = auth.currentUser;
              
              if (currentUser) {
                await setDoc(userRef, {
                  uid: currentUser.uid,
                  email: currentUser.email || '',
                  displayName: currentUser.displayName || '',
                  photoURL: currentUser.photoURL || '',
                  role: 'user',
                  membershipStatus: 'active',
                  createdAt: Date.now(),
                  updatedAt: Date.now()
                });
                logger.info('✅ Created user profile with default role', { uid });
              }
            } catch (createError) {
              logger.error('❌ Error creating user profile', createError);
            }
          } else {
            throw updateError; // Re-throw if it's not a permission issue
          }
        }
      }
    } else {
      // Create user profile with default values if it doesn't exist
      try {
        const auth = getAuth();
        const currentUser = auth.currentUser;
        
        if (currentUser) {
          await setDoc(userRef, {
            uid: currentUser.uid,
            email: currentUser.email || '',
            displayName: currentUser.displayName || '',
            photoURL: currentUser.photoURL || '',
            role: 'user',
            membershipStatus: 'active',
            createdAt: Date.now(),
            updatedAt: Date.now()
          });
          logger.info('✅ Created user profile with default role', { uid });
        }
      } catch (error) {
        logger.error('❌ Error creating user profile', error);
        throw error;
      }
    }
  } catch (error) {
    logger.error('❌ Error ensuring user role', error);
    throw error;
  }
};

/**
 * Check if a username is valid (format and availability)
 * @param username The username to validate
 * @returns Object with validation result and error message if invalid
 */
export const validateUsername = async (username: string): Promise<{isValid: boolean, error?: string}> => {
  // Check for minimum and maximum length
  if (username.length < USERNAME_MIN_LENGTH) {
    return { isValid: false, error: `Username must be at least ${USERNAME_MIN_LENGTH} characters` };
  }

  if (username.length > USERNAME_MAX_LENGTH) {
    return { isValid: false, error: `Username must be at most ${USERNAME_MAX_LENGTH} characters` };
  }

  // Check format (alphanumeric and underscores only)
  if (!USERNAME_REGEX.test(username)) {
    return { isValid: false, error: 'Username can only contain letters, numbers, and underscores' };
  }

  // Check if username is reserved
  if (RESERVED_USERNAMES.includes(username.toLowerCase())) {
    return { isValid: false, error: 'This username is reserved and cannot be used' };
  }

  try {
    // Check if username already exists in Firestore
    const usernameDoc = await getDoc(doc(db, 'usernames', username.toLowerCase()));
    if (usernameDoc.exists()) {
      return { isValid: false, error: 'Username is already taken' };
    }
    return { isValid: true };
  } catch (error) {
    logger.error('❌ Error checking username availability', error);
    return { isValid: false, error: 'Error checking username availability. Please try again.' };
  }
};

/**
 * Create or update a user profile in Firestore 
 * @param authUser Firebase Auth User object
 * @param profileData Profile data to set/update
 * @returns Updated user profile
 */
export const updateUserProfile = async (
  authUser: User, 
  profileData: Partial<Omit<UserProfile, 'uid' | 'email' | 'role' | 'createdAt' | 'updatedAt'>>
): Promise<Partial<UserProfile>> => {
  try {
    const userRef = doc(db, 'users', authUser.uid);
    const userSnap = await getDoc(userRef);
    
    if (userSnap.exists()) {
      // Update existing profile
      const updates: Partial<UserProfile> = {
        ...profileData,
        updatedAt: Date.now(),
      };
      
      // Validate introduction length
      if (profileData.introduction && profileData.introduction.length > 500) {
        throw new Error('introduction must be under 500 characters');
      }
      
      // Validate photo URLs limit
      if (profileData.photoUrls && profileData.photoUrls.length > 5) {
        throw new Error('Maximum 5 photos are allowed');
      }
      
      await updateDoc(userRef, updates);
      logger.info('✅ User profile updated successfully', { uid: authUser.uid });
      
      return {
        ...userSnap.data(),
        ...updates,
      } as Partial<UserProfile>;
      
    } else {
      logger.error('❌ User profile not found', { uid: authUser.uid });
      throw new Error('User profile not found');
    }
  } catch (error) {
    logger.error('❌ Error updating user profile', error);
    throw error;
  }
};

/**
 * Set a unique username for a user with transaction to ensure atomicity
 * @param authUser Firebase Auth User object
 * @param username Username to set (must be unique)
 * @returns Promise that resolves when username is set
 */
export const setUsername = async (authUser: User, username: string): Promise<void> => {
  const lowercaseUsername = username.toLowerCase();
  
  try {
    // Validate username first
    const validation = await validateUsername(username);
    if (!validation.isValid) {
      throw new Error(validation.error);
    }
    
    try {
      // Use a transaction to ensure atomicity when setting username
      await runTransaction(db, async (transaction) => {
        // Check if user already has a profile
        const userRef = doc(db, 'users', authUser.uid);
        const userSnap = await transaction.get(userRef);
        
        // Check if username is already taken
        const usernameRef = doc(db, 'usernames', lowercaseUsername);
        const usernameSnap = await transaction.get(usernameRef);
        
        if (usernameSnap.exists()) {
          throw new Error('Username is already taken');
        }
        
        // Create username mapping
        transaction.set(usernameRef, { uid: authUser.uid });          if (userSnap.exists()) {
          // If user has a profile, update the username
          
          // Allow username changes (removed restriction)
          transaction.update(userRef, { 
            username: username,
            updatedAt: Date.now()
          });
        } else {
          // Create a new user profile
          const newUserProfile: UserProfile = {
            uid: authUser.uid,
            email: authUser.email || '',
            username: username,
            displayName: authUser.displayName || '',
            photoURL: authUser.photoURL || '',
            role: 'user', // Default role
            membershipStatus: 'active', // Default status
            createdAt: Date.now(),
            updatedAt: Date.now()
          };
          
          transaction.set(userRef, newUserProfile);
        }
      });
    } catch (transactionError: any) {
      // If transaction fails with permission error, try individual operations
      if (transactionError.code === 'permission-denied') {
        logger.warn('⚠️ Permission denied in transaction, trying individual operations');
        
        // Check if username is already taken
        const usernameRef = doc(db, 'usernames', lowercaseUsername);
        const usernameSnap = await getDoc(usernameRef);
        
        if (usernameSnap.exists()) {
          throw new Error('Username is already taken');
        }
        
        // Create username mapping
        await setDoc(usernameRef, { uid: authUser.uid });
        
        // Create or update user profile
        const userRef = doc(db, 'users', authUser.uid);
        const userSnap = await getDoc(userRef);          if (userSnap.exists()) {
          // Allow username changes (removed restriction)
          await updateDoc(userRef, { 
            username: username,
            updatedAt: Date.now()
          });
        } else {
          // Create a new user profile
          const newUserProfile: UserProfile = {
            uid: authUser.uid,
            email: authUser.email || '',
            username: username,
            displayName: authUser.displayName || '',
            photoURL: authUser.photoURL || '',
            role: 'user', // Default role
            membershipStatus: 'active', // Default status
            createdAt: Date.now(),
            updatedAt: Date.now()
          };
          
          await setDoc(userRef, newUserProfile);
        }
      } else {
        // If not a permission error, rethrow
        throw transactionError;
      }
    }
    
    logger.info('✅ Username set successfully', { username, uid: authUser.uid });
  } catch (error) {
    logger.error('❌ Error setting username', error);
    throw error;
  }
};

/**
 * Update username for an existing user with proper cleanup of old mappings
 * @param authUser Firebase Auth User object
 * @param newUsername New username to set
 * @returns Promise that resolves when username is updated
 */
export const updateUsername = async (authUser: User, newUsername: string): Promise<void> => {
  const lowercaseNewUsername = newUsername.toLowerCase();
  
  try {
    // Validate new username first
    const validation = await validateUsername(newUsername);
    if (!validation.isValid) {
      throw new Error(validation.error);
    }
    
    // Get current user profile to find old username
    const userRef = doc(db, 'users', authUser.uid);
    const userSnap = await getDoc(userRef);
    
    if (!userSnap.exists()) {
      throw new Error('User profile not found');
    }
    
    const userData = userSnap.data() as UserProfile;
    const oldUsername = userData.username?.toLowerCase();
    
    // If username hasn't changed, no need to update
    if (oldUsername === lowercaseNewUsername) {
      return;
    }
    
    try {
      // Use a transaction to ensure atomicity when updating username
      await runTransaction(db, async (transaction) => {
        // Check if new username is already taken
        const newUsernameRef = doc(db, 'usernames', lowercaseNewUsername);
        const newUsernameSnap = await transaction.get(newUsernameRef);
        
        if (newUsernameSnap.exists()) {
          throw new Error('Username is already taken');
        }
        
        // Update user profile with new username
        transaction.update(userRef, { 
          username: newUsername,
          updatedAt: Date.now()
        });
        
        // Create new username mapping
        transaction.set(newUsernameRef, { uid: authUser.uid });
        
        // Delete old username mapping if it exists
        if (oldUsername) {
          const oldUsernameRef = doc(db, 'usernames', oldUsername);
          transaction.delete(oldUsernameRef);
        }
      });
      
      logger.success('✅ Username updated successfully', { 
        uid: authUser.uid, 
        oldUsername, 
        newUsername 
      });
      
    } catch (transactionError: any) {
      // If transaction fails with permission error, try individual operations
      if (transactionError.code === 'permission-denied') {
        logger.warn('⚠️ Permission denied in transaction, trying individual operations');
        
        // Check if new username is already taken
        const newUsernameRef = doc(db, 'usernames', lowercaseNewUsername);
        const newUsernameSnap = await getDoc(newUsernameRef);
        
        if (newUsernameSnap.exists()) {
          throw new Error('Username is already taken');
        }
        
        // Update user profile with new username
        await updateDoc(userRef, { 
          username: newUsername,
          updatedAt: Date.now()
        });
        
        // Create new username mapping
        await setDoc(newUsernameRef, { uid: authUser.uid });
          // Delete old username mapping if it exists
        if (oldUsername) {
          const oldUsernameRef = doc(db, 'usernames', oldUsername);
          try {
            await deleteDoc(oldUsernameRef);
          } catch (deleteError) {
            logger.warn('⚠️ Could not delete old username mapping, continuing', { oldUsername });
          }
        }
        
        logger.success('✅ Username updated successfully (individual operations)', { 
          uid: authUser.uid, 
          oldUsername, 
          newUsername 
        });
        
      } else {
        throw transactionError;
      }
    }
    
  } catch (error) {
    logger.error('❌ Error updating username', { 
      uid: authUser.uid, 
      newUsername, 
      error 
    });
    throw error;
  }
};

/**
 * Get a user profile by UID
 * @param uid User ID
 * @returns User profile or null if not found
 */
export const getUserProfileByUid = async (uid: string): Promise<UserProfile | null> => {
  try {
    const userDoc = await getDoc(doc(db, 'users', uid));
    if (userDoc.exists()) {
      return userDoc.data() as UserProfile;
    }
    return null;
  } catch (error) {
    logger.error('❌ Error getting user profile by UID', error);
    throw error;
  }
};

/**
 * Get a user profile by username
 * @param username Username to look up
 * @returns User profile or null if not found
 */
export const getUserProfileByUsername = async (username: string): Promise<UserProfile | null> => {
  try {
    const lowercaseUsername = username.toLowerCase();
    const usernameDoc = await getDoc(doc(db, 'usernames', lowercaseUsername));
    
    if (!usernameDoc.exists()) {
      return null;
    }
    
    const { uid } = usernameDoc.data();
    return getUserProfileByUid(uid);
  } catch (error) {
    logger.error('❌ Error getting user profile by username', error);
    throw error;
  }
};

/**
 * Get all user profiles from Firestore
 * @returns Promise with array of UserProfile objects
 */
export const getAllUsers = async (): Promise<UserProfile[]> => {
  try {
    const usersRef = collection(db, 'users');
    const q = query(usersRef, orderBy('createdAt', 'desc')); // Order by creation date, newest first
    const querySnapshot = await getDocs(q);
    
    const users: UserProfile[] = [];
    querySnapshot.forEach((doc) => {
      users.push(doc.data() as UserProfile);
    });
    
    logger.info('✅ All users fetched successfully', { count: users.length });
    return users;
  } catch (error) {
    logger.error('❌ Error getting all users', error);
    throw error;
  }
};

/**
 * Admin function to update a user's role
 * Can only be called by users with admin role
 * @param targetUid User ID to update
 * @param role New role to assign
 */
export const updateUserRole = async (targetUid: string, role: UserRole): Promise<void> => {
  try {
    logger.info('🔄 Calling updateUserRole function', { targetUid, role });
    const updateRoleCallable = httpsCallable(functions, 'updateUserRole'); // Renamed to avoid conflict
    const result = await updateRoleCallable({ uid: targetUid, role });
    logger.info('✅ User role updated successfully', { targetUid, role, result: result.data });
  } catch (error) {
    logger.error('❌ Error updating user role', error);
    throw error;
  }
};

/**
 * Update user membership status
 * Can only be done by admins in the UI, but enforced by Firestore rules
 * @param uid User ID to update
 * @param status New membership status
 */
export const updateMembershipStatus = async (uid: string, status: MembershipStatus): Promise<void> => {
  try {
    const userRef = doc(db, 'users', uid);
    await updateDoc(userRef, { 
      membershipStatus: status,
      updatedAt: Date.now()
    });
    logger.info('✅ User membership status updated successfully', { uid, status });
  } catch (error) {
    logger.error('❌ Error updating membership status', error);
    throw error;
  }
};

/**
 * Calls the 'deleteUserAccount' cloud function to permanently delete a user's account
 * and all associated data.
 * @param payload Object containing uid of the user to delete and an optional reason.
 * @param payload.uid UID of the user to be deleted.
 * @param payload.reason Optional reason for deletion (for admin-initiated deletions).
 * @returns Promise resolving with the result from the cloud function.
 */
export const requestDeleteUserAccount = async (payload: { 
  uid: string; 
  reason?: string; 
}): Promise<any> => { // Consider defining a more specific return type based on function's actual response
  try {
    logger.info('🔄 Calling deleteUserAccount function', payload);
    const deleteFunction = httpsCallable(functions, 'deleteUserAccount');
    const result = await deleteFunction(payload);
    logger.info('✅ User account deletion process initiated successfully', { uid: payload.uid, result: result.data });
    return result.data; // Or result directly if the whole HttpsCallableResult is needed
  } catch (error) {
    logger.error('❌ Error calling deleteUserAccount function', { uid: payload.uid, error });
    // Re-throw the error so the UI component can catch it and display a message
    // The error object from httpsCallable will have details like code and message
    throw error;
  }
};
